import secureStorage from '../lib/secureStorage';
import React, { useState, useEffect, useCallback } from 'react';
import authedFetch from '../lib/authedFetch';
import {
  BarChart3,
  TrendingUp,
  TrendingDown,
  Award,
  Target,
  Users,
  Building2,
  ChevronRight,
  Info,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Share2,
  ArrowUpRight,
  Sparkles,
  Calendar,
  Flag,
  Plus,
  Trash2,
  LineChart,
  Mail,
  Bell,
  BellOff,
  Lightbulb,
  BookOpen
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { PageShell } from '../components/layout/PageShell';
import { PageHero } from '../components/layout/PageHero';
import { getLightSectionTheme } from '../lib/sectionThemes';
import NavigationAdvantageCard from '../components/benchmarking/NavigationAdvantageCard';
import TopPerformersCard from '../components/benchmarking/TopPerformersCard';
import ValueTransferTab from '../components/benchmarking/ValueTransferTab';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { TileInfoButton } from '../components/common/TileInfoButton';


// Single source of truth for the "what is this view, why does it
// matter, what should I do" copy on each Benchmarking tab. Same
// pattern as MaturityHealthPage / ROEHealthPage. Keep each `body`
// to ~3 sentences max — popover is 320px wide.
const TAB_INFO = {
  overview: {
    title: 'Overview tab',
    body: 'Snapshot of your standing across all four benchmark categories — KPI Gaps, LEI Scores, ROI Metrics, and Trends. Each card shows the count of benchmarks where you lead, lag, or sit at parity with peers. What to do: click any card to drill into that category — the goal is to find your single biggest peer-gap and tackle it first, not chase parity everywhere.',
  },
  trends: {
    title: 'Trends tab',
    body: 'Direction and velocity of your key metrics over time, compared against industry trajectory. Shows whether you are *closing* or *widening* the peer gap month over month. What to do: a flat line on a metric where peers are accelerating is a louder warning than a low absolute score — re-prioritise that metric in your next quarter.',
  },
  kpi_gaps: {
    title: 'KPI Gaps tab',
    body: 'Per-KPI percentile rank within your industry cohort — sales productivity, manufacturing efficiency, customer service ratings, etc. Red = bottom quartile, green = top quartile. What to do: KPI gaps are the most board-ready signal in this dashboard — pick the bottom-2 and assign owners + 90-day improvement plans before next QBR.',
  },
  lei_scores: {
    title: 'LEI Scores tab',
    body: 'Your Learning Effectiveness Index pillars (engagement, completion, behavior change, etc.) compared with peers in your industry. What to do: low LEI vs peers signals your learning *system* is leaking, not just your training content. See the LEI Reference + Maturity Health to identify which pillar is the bottleneck.',
  },
  roi_metrics: {
    title: 'ROI Metrics tab',
    body: 'Financial-impact metrics — training ROI, productivity uplift, retention savings — vs industry medians. What to do: this is where finance leaders look first. If your ROI metrics trail peers despite parity on KPI/LEI, the value-conversion layer (transfer + maturity) is the gap, not the inputs.',
  },
};
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import {
  LineChart as RechartsLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';
import { BenchmarkTrendsTab } from '../components/benchmarking/BenchmarkTrendsTab';
import { AddGoalDialog } from '../components/benchmarking/AddGoalDialog';
import logger from '../lib/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => {
  const token = secureStorage.getItem('session_token');
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
};

const CATEGORY_CONFIG = {
  kpi_gaps: {
    name: 'KPI Performance Gaps',
    mmName: 'Outcomes',
    icon: Target,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    description: 'Projected vs Actual KPI Improvement'
  },
  lei_scores: {
    name: 'LEI Measure Scores',
    mmName: 'Conditions',
    icon: BarChart3,
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-200',
    description: 'Learning Effectiveness Index (0-100)'
  },
  roi_metrics: {
    name: 'ROI Metrics',
    mmName: 'Value',
    icon: TrendingUp,
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-200',
    description: 'Return on Investment & Payback'
  }
};

// Overview card ordering — teaches the value transfer narrative
// (Conditions → Outcomes → Value) without changing the underlying
// benchmark structure or tab layout.
const OVERVIEW_ORDER = ['lei_scores', 'kpi_gaps', 'roi_metrics'];

function PercentileBar({ percentile, showLabels = true }) {
  const getColor = () => {
    if (percentile >= 90) return 'bg-emerald-500';
    if (percentile >= 75) return 'bg-emerald-500';
    if (percentile >= 50) return 'bg-blue-500';
    if (percentile >= 25) return 'bg-amber-500';
    return 'bg-rose-500';
  };

  return (
    <div className="w-full">
      {showLabels && (
        <div className="flex justify-between text-xs text-slate-400 mb-1">
          <span>0</span>
          <span>25th</span>
          <span>50th</span>
          <span>75th</span>
          <span>90th+</span>
        </div>
      )}
      <div className="h-3 bg-slate-100 rounded-full relative overflow-hidden">
        <div className="absolute inset-y-0 left-0 w-1/4 bg-rose-100 border-r border-slate-200" />
        <div className="absolute inset-y-0 left-1/4 w-1/4 bg-amber-50 border-r border-slate-200" />
        <div className="absolute inset-y-0 left-1/2 w-1/4 bg-blue-50 border-r border-slate-200" />
        <div className="absolute inset-y-0 left-3/4 w-[15%] bg-emerald-50 border-r border-slate-200" />
        <div className="absolute inset-y-0 right-0 w-[10%] bg-emerald-50" />
        <div
          className={`absolute inset-y-0 left-0 ${getColor()} transition-all duration-500`}
          style={{ width: `${Math.min(percentile, 100)}%` }}
        />
        <div
          className="absolute top-0 bottom-0 w-0.5 bg-slate-800"
          style={{ left: `${Math.min(percentile, 100)}%` }}
        />
      </div>
    </div>
  );
}

function PerformanceBadge({ label }) {
  const config = {
    'Top Performer': { color: 'bg-emerald-100 text-emerald-800 border-emerald-300', icon: Award },
    'Above Average': { color: 'bg-emerald-100 text-emerald-800 border-emerald-300', icon: TrendingUp },
    'Average': { color: 'bg-blue-100 text-blue-800 border-blue-300', icon: Target },
    'Below Average': { color: 'bg-amber-100 text-amber-800 border-amber-300', icon: TrendingDown },
    'Needs Improvement': { color: 'bg-rose-100 text-rose-800 border-rose-300', icon: AlertCircle }
  };

  const { color, icon: Icon } = config[label] || config['Average'];

  return (
    <Badge className={`${color} border flex items-center gap-1`}>
      <Icon className="h-3 w-3" />
      {label}
    </Badge>
  );
}

export default function BenchmarkingPage() {
  const [loading, setLoading] = useState(true);
  const [industries, setIndustries] = useState([]);
  const [selectedIndustry, setSelectedIndustry] = useState('technology');
  // Region narrows the user-contributed slice of the cohort. The
  // pre-seeded percentiles aren't region-scoped today, so a region
  // filter only re-blends if there are ≥5 contributions tagged with
  // it. "all" = no region narrowing.
  const [regions, setRegions] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState('all');
  const [benchmarkData, setBenchmarkData] = useState(null);
  const [myPosition, setMyPosition] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [includeUserContributed, setIncludeUserContributed] = useState(true);
  const [contributeDialogOpen, setContributeDialogOpen] = useState(false);
  
  // Trends state
  const [trendsData, setTrendsData] = useState(null);
  const [trendsDays, setTrendsDays] = useState(30);
  const [showMultiLine, setShowMultiLine] = useState(false);
  
  // Goals state
  const [goals, setGoals] = useState([]);
  const [goalsProgress, setGoalsProgress] = useState(null);
  const [goalDialogOpen, setGoalDialogOpen] = useState(false);
  const [narrativeInsights, setNarrativeInsights] = useState(null);
  const [newGoal, setNewGoal] = useState({ 
    target_percentile: 75, 
    metric_id: null,
    email_notifications: true,
    drop_alert_threshold: 10,
    milestone_alerts: true
  });
  const [notificationHistory, setNotificationHistory] = useState([]);

  const fetchIndustries = useCallback(async () => {
    try {
      const [indResp, regResp] = await Promise.all([
        authedFetch(`${API_URL}/api/benchmarking/industries`, { headers: getAuthHeaders() }),
        authedFetch(`${API_URL}/api/benchmarking/regions`, { headers: getAuthHeaders() }),
      ]);
      if (indResp.ok) {
        const data = await indResp.json();
        setIndustries(data.industries);
      }
      if (regResp.ok) {
        const rdata = await regResp.json();
        setRegions(rdata.regions || []);
      }
    } catch (error) {
      logger.error('Error fetching industries:', error);
    }
  }, []);

  const fetchBenchmarkData = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        industry: selectedIndustry,
        include_user_contributed: String(includeUserContributed),
      });
      if (selectedRegion && selectedRegion !== 'all') {
        params.set('region', selectedRegion);
      }
      const response = await authedFetch(
        `${API_URL}/api/benchmarking/data?${params.toString()}`,
        { headers: getAuthHeaders() }
      );
      if (response.ok) {
        const data = await response.json();
        setBenchmarkData(data);
      }
    } catch (error) {
      logger.error('Error fetching benchmark data:', error);
    }
  }, [selectedIndustry, includeUserContributed, selectedRegion]);

  const fetchMyPosition = useCallback(async () => {
    try {
      const response = await authedFetch(
        `${API_URL}/api/benchmarking/my-position?industry=${selectedIndustry}`,
        { headers: getAuthHeaders() }
      );
      if (response.ok) {
        const data = await response.json();
        setMyPosition(data);
        
        // Record snapshot on position view
        if (data.has_data) {
          authedFetch(`${API_URL}/api/benchmarking/record-snapshot?industry=${selectedIndustry}`, {
            method: 'POST',
            headers: getAuthHeaders()
          }).catch(logger.error);
        }
      }
    } catch (error) {
      logger.error('Error fetching my position:', error);
    }
  }, [selectedIndustry]);
  
  const fetchNarrativeInsights = useCallback(async () => {
    try {
      const response = await authedFetch(
        `${API_URL}/api/benchmarking/narrative-insights?industry=${selectedIndustry}`,
        { headers: getAuthHeaders() }
      );
      if (response.ok) {
        const data = await response.json();
        setNarrativeInsights(data);
      }
    } catch (error) {
      logger.error('Error fetching narrative insights:', error);
    }
  }, [selectedIndustry]);
  
  const fetchTrends = useCallback(async () => {
    try {
      const response = await authedFetch(
        `${API_URL}/api/benchmarking/trends?days=${trendsDays}`,
        { headers: getAuthHeaders() }
      );
      if (response.ok) {
        const data = await response.json();
        setTrendsData(data);
      }
    } catch (error) {
      logger.error('Error fetching trends:', error);
    }
  }, [trendsDays]);
  
  const fetchGoals = useCallback(async () => {
    try {
      const response = await authedFetch(`${API_URL}/api/benchmarking/goals`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setGoals(data.goals || []);
      }
    } catch (error) {
      logger.error('Error fetching goals:', error);
    }
  }, []);
  
  const fetchGoalsProgress = useCallback(async () => {
    try {
      const response = await authedFetch(`${API_URL}/api/benchmarking/goals/progress`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setGoalsProgress(data);
      }
    } catch (error) {
      logger.error('Error fetching goals progress:', error);
    }
  }, []);
  
  const fetchNotificationHistory = useCallback(async () => {
    try {
      const response = await authedFetch(`${API_URL}/api/benchmarking/notifications/history?limit=10`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setNotificationHistory(data.notifications || []);
      }
    } catch (error) {
      logger.error('Error fetching notification history:', error);
    }
  }, []);
  
  const createGoal = async () => {
    try {
      const params = new URLSearchParams({
        target_percentile: newGoal.target_percentile.toString(),
        email_notifications: newGoal.email_notifications.toString(),
        drop_alert_threshold: newGoal.drop_alert_threshold.toString(),
        milestone_alerts: newGoal.milestone_alerts.toString()
      });
      if (newGoal.metric_id) {
        params.append('metric_id', newGoal.metric_id);
      }
      
      const response = await authedFetch(`${API_URL}/api/benchmarking/goals?${params}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        toast.success('Goal created with notifications enabled!');
        setGoalDialogOpen(false);
        setNewGoal({ 
          target_percentile: 75, 
          metric_id: null,
          email_notifications: true,
          drop_alert_threshold: 10,
          milestone_alerts: true
        });
        fetchGoals();
        fetchGoalsProgress();
      } else {
        toast.error('Failed to create goal');
      }
    } catch (error) {
      logger.error('Error creating goal:', error);
      toast.error('Failed to create goal');
    }
  };
  
  const deleteGoal = async (goalId) => {
    try {
      const response = await authedFetch(`${API_URL}/api/benchmarking/goals/${goalId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        toast.success('Goal deleted');
        fetchGoals();
        fetchGoalsProgress();
      }
    } catch (error) {
      logger.error('Error deleting goal:', error);
    }
  };

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await fetchIndustries();
      setLoading(false);
    };
    init();
  }, [fetchIndustries]);

  useEffect(() => {
    if (selectedIndustry) {
      fetchBenchmarkData();
      fetchMyPosition();
      fetchNarrativeInsights();
    }
  }, [selectedIndustry, includeUserContributed, selectedRegion, fetchBenchmarkData, fetchMyPosition, fetchNarrativeInsights]);
  
  useEffect(() => {
    fetchTrends();
  }, [trendsDays, fetchTrends]);
  
  useEffect(() => {
    fetchGoals();
    fetchGoalsProgress();
    fetchNotificationHistory();
  }, [fetchGoals, fetchGoalsProgress, fetchNotificationHistory]);

  const groupBenchmarksByCategory = (benchmarks) => {
    if (!benchmarks) return {};
    return benchmarks.reduce((acc, b) => {
      if (!acc[b.category]) acc[b.category] = [];
      acc[b.category].push(b);
      return acc;
    }, {});
  };

  const groupedBenchmarks = groupBenchmarksByCategory(benchmarkData?.benchmarks);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  const theme = getLightSectionTheme('Navy');

  // "Your Position" is now promoted into the hero stats grid so the
  // top of the page communicates rank at a glance instead of waiting
  // for the user to scan the body card.
  const positionHeroStats = (myPosition?.has_data && myPosition.summary) ? [
    {
      label: `Navigation Position · ${myPosition.industry_name || 'industry'}`,
      value: `${myPosition.summary.average_percentile_rank}th`,
      testId: 'benchmark-stat-percentile',
    },
    {
      label: 'Navigation Rating',
      value: (myPosition.summary.overall_performance || '—')
        .toString()
        .replace(/_/g, ' ')
        .replace(/\b\w/g, c => c.toUpperCase()),
      testId: 'benchmark-stat-rating',
    },
    {
      label: 'Value Transfer Advantage',
      value: `${myPosition.summary.above_median_count} ▲ / ${myPosition.summary.below_median_count} ▼`,
      testId: 'benchmark-stat-median-split',
    },
    {
      label: 'Competitive Strengths',
      value: myPosition.summary.top_performer_count,
      testId: 'benchmark-stat-top-performer',
    },
  ] : null;

  return (
    <PageShell
      theme={theme}
      hero={(
        <PageHero
          title="Industry Benchmarks"
          description="Compare your value transfer performance against industry peers and identify where stronger navigation conditions create competitive advantage."
          theme={theme}
          icon={Building2}
          stats={positionHeroStats}
          actions={(
            <>
              <div className="flex items-center gap-2 text-slate-700">
                <Label htmlFor="include-user-data" className="text-xs text-slate-700 font-medium">
                  Community data
                </Label>
                <Switch
                  id="include-user-data"
                  checked={includeUserContributed}
                  onCheckedChange={setIncludeUserContributed}
                />
              </div>
              <Select value={selectedIndustry} onValueChange={setSelectedIndustry}>
                <SelectTrigger className="w-[200px] bg-white border-slate-200 text-slate-700" data-testid="industry-select">
                  <Building2 className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Select industry" />
                </SelectTrigger>
                <SelectContent>
                  {industries.map((ind) => (
                    <SelectItem key={ind.id} value={ind.id}>{ind.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={selectedRegion} onValueChange={setSelectedRegion}>
                <SelectTrigger className="w-[160px] bg-white border-slate-200 text-slate-700" data-testid="region-select">
                  <SelectValue placeholder="Region" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All regions</SelectItem>
                  {regions.map((r) => (
                    <SelectItem key={r} value={r}>{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                onClick={() => setContributeDialogOpen(true)}
                className={`${theme.actionBtn} border-0`}
                data-testid="contribute-btn"
              >
                <Share2 className="h-4 w-4 mr-2" />
                Contribute Data
              </Button>
              <Link
                to="/benchmarks-reference"
                className="inline-flex items-center gap-1.5 text-xs font-semibold bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 px-3 py-1.5 rounded-md transition-colors"
                data-testid="industry-benchmarks-references-link"
              >
                <BookOpen className="h-3.5 w-3.5" />
                References
              </Link>
            </>
          )}
          testId="benchmarking-hero"
        />
      )}
    >
      <div className="space-y-6" data-testid="benchmarking-page">

      {!myPosition?.has_data && (
        <Card className="bg-gradient-to-r from-slate-50 to-blue-50 border border-slate-200">
          <CardContent className="py-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Info className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-800">No Data Yet</h3>
                <p className="text-sm text-slate-600 mt-1">
                  Import KPI actuals, LEI measure data, or run calculations to see your position against industry benchmarks.
                </p>
                <Button variant="link" className="p-0 h-auto mt-2 text-blue-600">
                  Go to Gap Analysis <ArrowUpRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-6 lg:w-auto lg:inline-flex">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="trends" data-testid="trends-tab">Trends</TabsTrigger>
          <TabsTrigger value="kpi_gaps">KPI Gaps</TabsTrigger>
          <TabsTrigger value="lei_scores">LEI Scores</TabsTrigger>
          <TabsTrigger value="roi_metrics">ROI Metrics</TabsTrigger>
          <TabsTrigger value="value_transfer" data-testid="value-transfer-tab">Value Transfer</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-6">
          <div className="flex items-center gap-2 mb-3 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
            <BarChart3 className="h-3 w-3" /> Overview
            <TileInfoButton
              testid="benchmark-tab-info-overview"
              title={TAB_INFO.overview.title}
              body={TAB_INFO.overview.body}
            />
          </div>
          <div className="flex items-center gap-1.5 mb-3 text-xs text-slate-500" data-testid="overview-chain-narrative">
            <span className="font-semibold text-purple-700">Conditions</span>
            <ChevronRight className="h-3 w-3" />
            <span className="font-semibold text-blue-700">Outcomes</span>
            <ChevronRight className="h-3 w-3" />
            <span className="font-semibold text-emerald-700">Value</span>
            <span className="hidden sm:inline">— how navigation conditions convert into business outcomes and financial value.</span>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {OVERVIEW_ORDER.map((categoryId) => {
              const config = CATEGORY_CONFIG[categoryId];
              const categoryBenchmarks = groupedBenchmarks[categoryId] || [];
              const Icon = config.icon;
              
              return (
                <Card 
                  key={categoryId} 
                  className={`${config.bgColor} ${config.borderColor} border-2 cursor-pointer hover:shadow-md transition-shadow`}
                  onClick={() => setActiveTab(categoryId)}
                  data-testid={`category-card-${categoryId}`}
                >
                  <CardHeader>
                    <CardTitle className={`flex items-center gap-2 ${config.color}`}>
                      <Icon className="h-5 w-5" />
                      {config.mmName}
                      <span className="text-xs font-medium text-slate-500">({config.name})</span>
                    </CardTitle>
                    <CardDescription>{config.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {categoryBenchmarks.slice(0, 3).map((b) => (
                        <div key={b.metric_id} className="flex items-center justify-between text-sm">
                          <span className="text-slate-700">{b.metric_name}</span>
                          <span className="font-medium text-slate-900">
                            {b.metric_id === 'roi_percent' ? `${(b.percentile_50 / 100).toFixed(2)}x` :
                             categoryId === 'roi_metrics' && b.metric_id === 'payback_months' ? `${b.percentile_50.toFixed(1)} mo` :
                             `${b.percentile_50.toFixed(1)}%`}
                          </span>
                        </div>
                      ))}
                      {categoryBenchmarks.length > 3 && (
                        <p className="text-xs text-slate-500 flex items-center gap-1">
                          +{categoryBenchmarks.length - 3} more metrics
                          <ChevronRight className="h-3 w-3" />
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Navigation Advantage + What Top Performers Do Differently —
              synthesis of existing position/benchmark data, no new math. */}
          {(myPosition?.has_data || benchmarkData?.benchmarks?.length > 0) && (
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <NavigationAdvantageCard myPosition={myPosition} industryName={benchmarkData?.industry_name} />
              <TopPerformersCard benchmarks={benchmarkData?.benchmarks} industryName={benchmarkData?.industry_name} />
            </div>
          )}

          {/* Sample Size Info */}
          <div className="mt-6 flex items-center gap-2 text-sm text-slate-500">
            <Users className="h-4 w-4" />
            Based on {benchmarkData?.benchmarks?.[0]?.sample_size || 0}+ organizations in {benchmarkData?.industry_name}
            {includeUserContributed && (
              <Badge variant="outline" className="ml-2 text-xs">
                <CheckCircle2 className="h-3 w-3 mr-1" />
                Includes community data
              </Badge>
            )}
          </div>
          
          {/* Narrative Insights Panel */}
          {narrativeInsights?.insights?.length > 0 && (
            <div className="mt-8 space-y-4" data-testid="narrative-insights-panel">
              <div className="flex items-center gap-2 mb-2">
                <Lightbulb className="h-5 w-5 text-amber-500" />
                <h3 className="text-lg font-semibold text-slate-800">Benchmark Insights</h3>
                {narrativeInsights.measures_analyzed > 0 && (
                  <Badge variant="outline" className="text-xs">
                    {narrativeInsights.measures_analyzed} measures analyzed
                  </Badge>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {narrativeInsights.insights.map((insight, idx) => {
                  const typeConfig = {
                    strength: { border: 'border-emerald-200', bg: 'bg-emerald-50', icon: <TrendingUp className="h-4 w-4 text-emerald-600" />, label: 'Strength', labelColor: 'bg-emerald-100 text-emerald-700' },
                    opportunity: { border: 'border-amber-200', bg: 'bg-amber-50', icon: <Target className="h-4 w-4 text-amber-600" />, label: 'Opportunity', labelColor: 'bg-amber-100 text-amber-700' },
                    neutral: { border: 'border-blue-200', bg: 'bg-blue-50', icon: <BarChart3 className="h-4 w-4 text-blue-600" />, label: 'Analysis', labelColor: 'bg-blue-100 text-blue-700' },
                    info: { border: 'border-slate-200', bg: 'bg-slate-50', icon: <Info className="h-4 w-4 text-slate-500" />, label: 'Context', labelColor: 'bg-slate-100 text-slate-600' }
                  };
                  const cfg = typeConfig[insight.type] || typeConfig.info;
                  
                  return (
                    <Card key={insight.title || `insight-${idx}`} className={`${cfg.border} ${cfg.bg}`} data-testid={`insight-card-${idx}`}>
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <div className="mt-0.5 shrink-0">{cfg.icon}</div>
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <Badge className={`text-xs ${cfg.labelColor}`}>{cfg.label}</Badge>
                            </div>
                            <h4 className="font-medium text-sm text-slate-800 mb-1">{insight.title}</h4>
                            <p className="text-xs text-slate-600 leading-relaxed">{insight.body}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          )}
        </TabsContent>
        
        {/* Trends Tab */}
        <TabsContent value="trends" className="mt-6">
          <div className="flex items-center gap-2 mb-3 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
            <LineChart className="h-3 w-3" /> Trends
            <TileInfoButton
              testid="benchmark-tab-info-trends"
              title={TAB_INFO.trends.title}
              body={TAB_INFO.trends.body}
            />
          </div>
          <BenchmarkTrendsTab
            goals={goals}
            goalsProgress={goalsProgress}
            trendsData={trendsData}
            trendsDays={trendsDays}
            setTrendsDays={setTrendsDays}
            showMultiLine={showMultiLine}
            setShowMultiLine={setShowMultiLine}
            setGoalDialogOpen={setGoalDialogOpen}
            deleteGoal={deleteGoal}
            notificationHistory={notificationHistory}
          />
        </TabsContent>

        {/* Value Transfer Tab — synthesis of existing percentiles
            arranged along the chain. */}
        <TabsContent value="value_transfer" className="mt-6">
          <div className="flex items-center gap-2 mb-3 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
            <TrendingUp className="h-3 w-3" /> Value Transfer
          </div>
          <ValueTransferTab myPosition={myPosition} industryName={benchmarkData?.industry_name} />
        </TabsContent>

        {/* Category Tabs */}
        {['kpi_gaps', 'lei_scores', 'roi_metrics'].map((categoryId) => (
          <TabsContent key={categoryId} value={categoryId} className="mt-6">
            <div className="flex items-center gap-2 mb-3 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
              {React.createElement(CATEGORY_CONFIG[categoryId].icon, { className: 'h-3 w-3' })}
              {CATEGORY_CONFIG[categoryId].label}
              <TileInfoButton
                testid={`benchmark-tab-info-${categoryId}`}
                title={TAB_INFO[categoryId].title}
                body={TAB_INFO[categoryId].body}
              />
            </div>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  {React.createElement(CATEGORY_CONFIG[categoryId].icon, { 
                    className: `h-5 w-5 ${CATEGORY_CONFIG[categoryId].color}` 
                  })}
                  {CATEGORY_CONFIG[categoryId].name}
                </CardTitle>
                <CardDescription>
                  {CATEGORY_CONFIG[categoryId].description} - {benchmarkData?.industry_name} Industry
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {(groupedBenchmarks[categoryId] || []).map((benchmark) => {
                    const myComparison = myPosition?.comparisons?.find(c => c.metric_id === benchmark.metric_id);
                    
                    return (
                      <div 
                        key={benchmark.metric_id} 
                        className="p-4 border rounded-lg bg-white hover:bg-slate-50 transition-colors"
                        data-testid={`benchmark-${benchmark.metric_id}`}
                      >
                        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-3">
                          <div>
                            <h4 className="font-medium text-slate-900">{benchmark.metric_name}</h4>
                            <p className="text-sm text-slate-500">
                              Sample: {benchmark.sample_size} organizations
                            </p>
                          </div>
                          {myComparison && (
                            <div className="flex items-center gap-3">
                              <PerformanceBadge label={myComparison.performance_label} />
                              <span className="text-sm">
                                Your value: <strong>{benchmark.metric_id === 'roi_percent' ? `${(myComparison.your_value / 100).toFixed(2)}x` : myComparison.your_value}</strong>
                                {benchmark.metric_id === 'roi_percent' ? '' : categoryId === 'roi_metrics' && benchmark.metric_id === 'payback_months' ? ' mo' : '%'}
                              </span>
                            </div>
                          )}
                        </div>
                        
                        {/* Percentile Distribution */}
                        <div className="space-y-2">
                          {myComparison && (
                            <div className="mb-4">
                              <div className="flex items-center justify-between text-sm mb-1">
                                <span className="text-slate-600">Your percentile rank</span>
                                <span className="font-bold text-indigo-600">
                                  {myComparison.percentile_rank}th percentile
                                </span>
                              </div>
                              <PercentileBar percentile={myComparison.percentile_rank} />
                            </div>
                          )}
                          
                          <div className="grid grid-cols-4 gap-4 mt-4">
                            <div className="text-center p-2 bg-rose-50 rounded">
                              <p className="text-xs text-slate-500">25th</p>
                              <p className="font-semibold text-rose-700">
                                {benchmark.metric_id === 'roi_percent' ? `${(benchmark.percentile_25 / 100).toFixed(2)}x` :
                                 `${benchmark.percentile_25}${categoryId === 'roi_metrics' && benchmark.metric_id === 'payback_months' ? '' : '%'}`}
                              </p>
                            </div>
                            <div className="text-center p-2 bg-amber-50 rounded">
                              <p className="text-xs text-slate-500">Median</p>
                              <p className="font-semibold text-amber-700">
                                {benchmark.metric_id === 'roi_percent' ? `${(benchmark.percentile_50 / 100).toFixed(2)}x` :
                                 `${benchmark.percentile_50}${categoryId === 'roi_metrics' && benchmark.metric_id === 'payback_months' ? '' : '%'}`}
                              </p>
                            </div>
                            <div className="text-center p-2 bg-emerald-50 rounded">
                              <p className="text-xs text-slate-500">75th</p>
                              <p className="font-semibold text-emerald-700">
                                {benchmark.metric_id === 'roi_percent' ? `${(benchmark.percentile_75 / 100).toFixed(2)}x` :
                                 `${benchmark.percentile_75}${categoryId === 'roi_metrics' && benchmark.metric_id === 'payback_months' ? '' : '%'}`}
                              </p>
                            </div>
                            <div className="text-center p-2 bg-emerald-50 rounded">
                              <p className="text-xs text-slate-500">90th</p>
                              <p className="font-semibold text-emerald-700">
                                {benchmark.metric_id === 'roi_percent' ? `${(benchmark.percentile_90 / 100).toFixed(2)}x` :
                                 `${benchmark.percentile_90}${categoryId === 'roi_metrics' && benchmark.metric_id === 'payback_months' ? '' : '%'}`}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>

      {/* Contribute Dialog */}
      <Dialog open={contributeDialogOpen} onOpenChange={setContributeDialogOpen}>
        <DialogContent data-testid="contribute-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Share2 className="h-5 w-5 text-emerald-600" />
              Contribute to Benchmarks
            </DialogTitle>
            <DialogDescription>
              Help improve industry benchmarks by contributing your anonymized data. 
              Your organization's identity is never revealed.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="space-y-4">
              <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                <h4 className="font-medium text-emerald-800 flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4" />
                  What gets shared (anonymized)
                </h4>
                <ul className="mt-2 text-sm text-emerald-700 space-y-1">
                  <li>• KPI improvement percentages</li>
                  <li>• LEI measure scores</li>
                  <li>• ROI metrics (ROI%, payback period)</li>
                  <li>• Industry classification & company size</li>
                </ul>
              </div>
              <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                <h4 className="font-medium text-slate-700">What's protected</h4>
                <ul className="mt-2 text-sm text-slate-600 space-y-1">
                  <li>• Your organization name</li>
                  <li>• User identities</li>
                  <li>• Specific program details</li>
                  <li>• Raw financial numbers</li>
                </ul>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setContributeDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={() => {
                toast.success("Thank you! Your anonymized data has been contributed.");
                setContributeDialogOpen(false);
              }}
            >
              <Share2 className="h-4 w-4 mr-2" />
              Contribute My Data
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Add Goal Dialog */}
      <AddGoalDialog
        goalDialogOpen={goalDialogOpen}
        setGoalDialogOpen={setGoalDialogOpen}
        newGoal={newGoal}
        setNewGoal={setNewGoal}
        createGoal={createGoal}
      />
      </div>
    </PageShell>
  );
}
